import { useState } from 'react';
import { Layout } from '../components/Layout';
import { motion } from 'framer-motion';
import { 
  Calculator, TrendingUp, PiggyBank, Target, 
  Percent, ArrowRight, Sparkles, Clock
} from 'lucide-react';
import { 
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Area, AreaChart
} from 'recharts';
import axios from 'axios';
import { CalcActions } from '../components/CalcActions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Tools() {
  const [activeTab, setActiveTab] = useState('compound');
  
  // Compound Interest Calculator State
  const [principal, setPrincipal] = useState(1000);
  const [monthly, setMonthly] = useState(500);
  const [years, setYears] = useState(20);
  const [rate, setRate] = useState(10);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const calculateCompound = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/calculator/compound?principal=${principal}&monthly_contribution=${monthly}&years=${years}&annual_rate=${rate}`);
      setResult(res.data);
    } catch (error) {
      console.error('Calculation error:', error);
    }
    setLoading(false);
  };

  const formatMoney = (num) => {
    if (num >= 1000000) return `R${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `R${(num / 1000).toFixed(0)}K`;
    return `R${num}`;
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8 max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-4 mb-2">
            <div className="w-14 h-14 bg-[#C0975C] flex items-center justify-center">
              <Calculator className="w-7 h-7 text-black" />
            </div>
            <div>
              <h1 className="text-3xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Financial <span className="text-[#C0975C]">Tools</span>
              </h1>
              <p className="text-[#A3A3A3]">See your money grow with these calculators</p>
            </div>
          </div>
        </motion.div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {[
            { id: 'compound', label: 'Compound Interest', icon: TrendingUp },
            { id: 'savings', label: '50/30/20 Budget', icon: PiggyBank },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 font-bold whitespace-nowrap transition-all ${
                activeTab === tab.id 
                  ? 'bg-[#C0975C] text-black' 
                  : 'bg-white/5 text-white hover:bg-white/10'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Compound Interest Calculator */}
        {activeTab === 'compound' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid lg:grid-cols-2 gap-6"
          >
            {/* Input Card */}
            <div className="neo-card p-6">
              <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#C0975C]" />
                Compound Interest
              </h2>
              
              <div className="space-y-6">
                {/* Starting Amount */}
                <div>
                  <label className="block text-sm text-[#A3A3A3] mb-2">Starting Amount</label>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold text-[#C0975C]">R</span>
                    <input
                      type="range"
                      min="0"
                      max="50000"
                      step="500"
                      value={principal}
                      onChange={(e) => setPrincipal(Number(e.target.value))}
                      className="flex-1 accent-[#C0975C]"
                    />
                    <span className="text-xl font-bold w-24 text-right">{principal.toLocaleString()}</span>
                  </div>
                </div>

                {/* Monthly Contribution */}
                <div>
                  <label className="block text-sm text-[#A3A3A3] mb-2">Monthly Contribution</label>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold text-[#0A1F44]">R</span>
                    <input
                      type="range"
                      min="0"
                      max="5000"
                      step="100"
                      value={monthly}
                      onChange={(e) => setMonthly(Number(e.target.value))}
                      className="flex-1 accent-[#0A1F44]"
                    />
                    <span className="text-xl font-bold w-24 text-right">{monthly.toLocaleString()}</span>
                  </div>
                </div>

                {/* Years */}
                <div>
                  <label className="block text-sm text-[#A3A3A3] mb-2">Investment Period</label>
                  <div className="flex items-center gap-3">
                    <Clock className="w-6 h-6 text-[#C0975C]" />
                    <input
                      type="range"
                      min="1"
                      max="40"
                      value={years}
                      onChange={(e) => setYears(Number(e.target.value))}
                      className="flex-1 accent-[#C0975C]"
                    />
                    <span className="text-xl font-bold w-24 text-right">{years} years</span>
                  </div>
                </div>

                {/* Interest Rate */}
                <div>
                  <label className="block text-sm text-[#A3A3A3] mb-2">Annual Return Rate</label>
                  <div className="flex items-center gap-3">
                    <Percent className="w-6 h-6 text-[#C0975C]" />
                    <input
                      type="range"
                      min="1"
                      max="20"
                      step="0.5"
                      value={rate}
                      onChange={(e) => setRate(Number(e.target.value))}
                      className="flex-1 accent-[#C0975C]"
                    />
                    <span className="text-xl font-bold w-24 text-right">{rate}%</span>
                  </div>
                </div>

                <button
                  onClick={calculateCompound}
                  disabled={loading}
                  className="neo-button-primary w-full py-4 font-bold text-lg flex items-center justify-center gap-2"
                >
                  {loading ? 'Calculating...' : 'Calculate Growth'}
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Results Card */}
            <div className="neo-card p-6">
              {result ? (
                <>
                  <h2 className="text-xl font-bold mb-6">Your Money After {years} Years</h2>
                  
                  {/* Big Number */}
                  <div className="text-center mb-6 p-6 bg-gradient-to-br from-[#C0975C]/20 to-transparent">
                    <p className="text-[#A3A3A3] text-sm mb-2">Future Value</p>
                    <p className="text-5xl font-black text-[#C0975C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {formatMoney(result.future_value)}
                    </p>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="p-4 bg-white/5">
                      <p className="text-[#A3A3A3] text-sm">You Invested</p>
                      <p className="text-2xl font-bold">{formatMoney(result.total_contributions)}</p>
                    </div>
                    <div className="p-4 bg-white/5">
                      <p className="text-[#A3A3A3] text-sm">Interest Earned</p>
                      <p className="text-2xl font-bold text-[#00FF88]">+{formatMoney(result.interest_earned)}</p>
                    </div>
                  </div>

                  {/* Chart */}
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={result.breakdown}>
                        <defs>
                          <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#C0975C" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#C0975C" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <XAxis 
                          dataKey="year" 
                          stroke="#666" 
                          tick={{ fill: '#666' }}
                          tickFormatter={(v) => `Y${v}`}
                        />
                        <YAxis 
                          stroke="#666" 
                          tick={{ fill: '#666' }}
                          tickFormatter={(v) => formatMoney(v)}
                        />
                        <Tooltip 
                          contentStyle={{ background: '#1a1a1a', border: '1px solid #333' }}
                          formatter={(value) => [`R${value.toLocaleString()}`, 'Value']}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="value" 
                          stroke="#C0975C" 
                          fillOpacity={1}
                          fill="url(#colorValue)"
                          strokeWidth={2}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="contributions" 
                          stroke="#0A1F44" 
                          strokeDasharray="5 5"
                          dot={false}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-[#A3A3A3] mt-2">
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 bg-[#C0975C]"></span> Total Value
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 bg-[#0A1F44]"></span> Your Contributions
                    </span>
                  </div>

                  <CalcActions
                    title="Compound Interest"
                    rows={[
                      { label: 'Starting amount', value: `R${principal.toLocaleString()}` },
                      { label: 'Monthly contribution', value: `R${monthly.toLocaleString()}` },
                      { label: 'Investment period', value: `${years} years` },
                      { label: 'Annual return rate', value: `${rate}%` },
                      { label: 'You invested', value: `R${Math.round(result.total_contributions).toLocaleString()}` },
                      { label: 'Interest earned', value: `R${Math.round(result.interest_earned).toLocaleString()}` },
                    ]}
                    result={{ label: 'Future value', value: `R${Math.round(result.future_value).toLocaleString()}` }}
                  />
                  <p className="text-xs leading-relaxed mt-3" style={{ color: '#8891a5' }} data-testid="compound-assumptions">
                    Illustrative estimate only. Assumes a constant {rate}% annual return, compounded monthly, before any fees, tax or inflation. Real returns vary and are not guaranteed. This is general education, not financial advice (FAIS).
                  </p>
                </>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center p-8">
                  <TrendingUp className="w-16 h-16 text-[#C0975C] mb-4" />
                  <h3 className="text-xl font-bold mb-2">See Your Money Grow</h3>
                  <p className="text-[#A3A3A3]">
                    Adjust the sliders and hit calculate to see the magic of compound interest!
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* 50/30/20 Budget Calculator */}
        {activeTab === 'savings' && (
          <BudgetCalculator />
        )}

        {/* Tip Box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-8 neo-card p-6 border-l-4 border-[#C0975C]"
        >
          <h3 className="font-bold mb-2">Did You Know?</h3>
          <p className="text-[#A3A3A3]">
            If you start investing R500/month at age 20, you could have over <span className="text-[#C0975C] font-bold">R3.2 million</span> by 
            retirement (at 10% annual return). Starting at 30 gives you only R1.4 million. 
            <span className="text-[#0A1F44]"> Starting early = R1.8 million more!</span>
          </p>
        </motion.div>
      </div>
    </Layout>
  );
}

// 50/30/20 Budget Calculator Component
function BudgetCalculator() {
  const [income, setIncome] = useState(15000);
  
  const needs = Math.round(income * 0.5);
  const wants = Math.round(income * 0.3);
  const savings = Math.round(income * 0.2);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="grid lg:grid-cols-2 gap-6"
    >
      {/* Input */}
      <div className="neo-card p-6">
        <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
          <PiggyBank className="w-5 h-5 text-[#0A1F44]" />
          50/30/20 Budget
        </h2>
        
        <div>
          <label className="block text-sm text-[#A3A3A3] mb-2">Your Monthly Income</label>
          <div className="flex items-center gap-3">
            <span className="text-3xl font-bold text-[#C0975C]">R</span>
            <input
              type="range"
              min="5000"
              max="100000"
              step="1000"
              value={income}
              onChange={(e) => setIncome(Number(e.target.value))}
              className="flex-1 accent-[#C0975C]"
            />
            <span className="text-2xl font-bold w-32 text-right">{income.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="neo-card p-6">
        <h2 className="text-xl font-bold mb-6">Your Budget Breakdown</h2>
        
        <div className="space-y-4">
          {/* Needs */}
          <div className="p-4 bg-gradient-to-r from-[#C0975C]/20 to-transparent">
            <div className="flex justify-between items-center mb-2">
              <span className="font-bold">50% Needs</span>
              <span className="text-2xl font-black text-[#C0975C]">R{needs.toLocaleString()}</span>
            </div>
            <div className="w-full bg-white/10 h-3">
              <div className="bg-[#C0975C] h-3" style={{ width: '50%' }}></div>
            </div>
            <p className="text-[#A3A3A3] text-sm mt-2">Rent, food, transport, utilities, insurance</p>
          </div>

          {/* Wants */}
          <div className="p-4 bg-gradient-to-r from-[#0A1F44]/20 to-transparent">
            <div className="flex justify-between items-center mb-2">
              <span className="font-bold">30% Wants</span>
              <span className="text-2xl font-black text-[#0A1F44]">R{wants.toLocaleString()}</span>
            </div>
            <div className="w-full bg-white/10 h-3">
              <div className="bg-[#0A1F44] h-3" style={{ width: '30%' }}></div>
            </div>
            <p className="text-[#A3A3A3] text-sm mt-2">Entertainment, dining out, shopping, hobbies</p>
          </div>

          {/* Savings */}
          <div className="p-4 bg-gradient-to-r from-[#C0975C]/20 to-transparent">
            <div className="flex justify-between items-center mb-2">
              <span className="font-bold">20% Savings</span>
              <span className="text-2xl font-black text-[#C0975C]">R{savings.toLocaleString()}</span>
            </div>
            <div className="w-full bg-white/10 h-3">
              <div className="bg-[#C0975C] h-3" style={{ width: '20%' }}></div>
            </div>
            <p className="text-[#A3A3A3] text-sm mt-2">Emergency fund, investments, debt repayment</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
